import React, { useState, useEffect } from 'react';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Clock,
  Zap,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { ApiStatusBadge } from '@/components/ApiConnectionStatus';
import { checkGoogleApisHealth } from '@/lib/api-health';

interface WebVital {
  id: string;
  site_url: string;
  site_name: string;
  performance_score: number;
  lcp: number;
  fid: number;
  cls: number;
  checked_at: string;
}

interface VitalStats {
  avgScore: number;
  avgLCP: number;
  avgFID: number;
  avgCLS: number;
  sitesMonitored: number;
  passingScore: number;
}

export default function WebVitalsPage() {
  const [vitals, setVitals] = useState<WebVital[]>([]);
  const [stats, setStats] = useState<VitalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('7d');

  useEffect(() => {
    loadVitals();
  }, [timeRange]);

  async function loadVitals(isRefresh = false) {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const daysAgo = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

      const { data, error } = await supabase
        .from('core_web_vitals')
        .select('*')
        .gte('checked_at', cutoffDate.toISOString())
        .order('checked_at', { ascending: false });

      if (error) throw error;

      setVitals(data || []);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error loading web vitals:', error);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }

  function calculateStats(data: WebVital[]) {
    if (data.length === 0) {
      setStats(null);
      return;
    }

    const avgScore = data.reduce((sum, v) => sum + v.performance_score, 0) / data.length;
    const avgLCP = data.reduce((sum, v) => sum + (v.lcp || 0), 0) / data.length;
    const avgFID = data.reduce((sum, v) => sum + (v.fid || 0), 0) / data.length;
    const avgCLS = data.reduce((sum, v) => sum + (v.cls || 0), 0) / data.length;
    
    // Count unique sites
    const uniqueSites = new Set(data.map(v => v.site_url)).size;
    
    // Count sites with score >= 90
    const passing = data.filter(v => v.performance_score >= 90).length;
    const passingScore = data.length > 0 ? (passing / data.length) * 100 : 0;

    setStats({
      avgScore: Math.round(avgScore),
      avgLCP: Math.round(avgLCP),
      avgFID: Math.round(avgFID),
      avgCLS: Math.round(avgCLS * 100) / 100,
      sitesMonitored: uniqueSites,
      passingScore: Math.round(passingScore),
    });
  }

  function getScoreColor(score: number) {
    if (score >= 90) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  }

  function getScoreBg(score: number) {
    if (score >= 90) return 'bg-green-50 border-green-200';
    if (score >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  }

  function getLCPStatus(lcp: number) {
    if (lcp <= 2500) return { label: 'Good', color: 'text-green-600' };
    if (lcp <= 4000) return { label: 'Needs Improvement', color: 'text-yellow-600' };
    return { label: 'Poor', color: 'text-red-600' };
  }

  function getFIDStatus(fid: number) {
    if (fid <= 100) return { label: 'Good', color: 'text-green-600' };
    if (fid <= 300) return { label: 'Needs Improvement', color: 'text-yellow-600' };
    return { label: 'Poor', color: 'text-red-600' };
  }

  function getCLSStatus(cls: number) {
    if (cls <= 0.1) return { label: 'Good', color: 'text-green-600' };
    if (cls <= 0.25) return { label: 'Needs Improvement', color: 'text-yellow-600' };
    return { label: 'Poor', color: 'text-red-600' };
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <Activity className="w-8 h-8" />
              Core Web Vitals
            </h1>
            <p className="text-gray-600">
              Monitor your website's performance metrics and user experience
            </p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <button
              onClick={() => loadVitals(true)}
              disabled={refreshing}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
        
        {/* API Connection Status */}
        <div className="mt-4 flex items-center gap-2">
          <span className="text-sm text-gray-600">PageSpeed API Status:</span>
          <ApiStatusBadge 
            apiName="Google PageSpeed API" 
            checkFunction={checkGoogleApisHealth}
          />
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Avg Performance</span>
              <Activity className="w-5 h-5 text-primary-600" />
            </div>
            <p className={`text-3xl font-bold ${getScoreColor(stats.avgScore)}`}>
              {stats.avgScore}
            </p>
            <p className="text-sm text-gray-500 mt-1">out of 100</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Avg LCP</span>
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <p className={`text-3xl font-bold ${getLCPStatus(stats.avgLCP).color}`}>
              {(stats.avgLCP / 1000).toFixed(2)}s
            </p>
            <p className="text-sm text-gray-500 mt-1">{getLCPStatus(stats.avgLCP).label}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Avg FID</span>
              <Zap className="w-5 h-5 text-yellow-600" />
            </div>
            <p className={`text-3xl font-bold ${getFIDStatus(stats.avgFID).color}`}>
              {stats.avgFID}ms
            </p>
            <p className="text-sm text-gray-500 mt-1">{getFIDStatus(stats.avgFID).label}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Sites Monitored</span>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.sitesMonitored}</p>
            <p className="text-sm text-gray-500 mt-1">{stats.passingScore}% passing</p>
          </div>
        </div>
      )}

      {/* Data Table */}
      {vitals.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Recent Measurements</h2>
            <p className="text-sm text-gray-600 mt-1">
              Showing {vitals.length} measurement{vitals.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LCP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    FID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CLS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Checked At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vitals.map((vital) => (
                  <tr key={vital.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {vital.site_name || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">{vital.site_url}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getScoreBg(vital.performance_score)}`}>
                        {vital.performance_score >= 90 ? (
                          <CheckCircle className="w-4 h-4 mr-1" />
                        ) : vital.performance_score >= 50 ? (
                          <AlertTriangle className="w-4 h-4 mr-1" />
                        ) : (
                          <TrendingDown className="w-4 h-4 mr-1" />
                        )}
                        <span className={getScoreColor(vital.performance_score)}>
                          {vital.performance_score}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className={`font-medium ${getLCPStatus(vital.lcp).color}`}>
                          {(vital.lcp / 1000).toFixed(2)}s
                        </div>
                        <div className="text-xs text-gray-500">
                          {getLCPStatus(vital.lcp).label}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className={`font-medium ${getFIDStatus(vital.fid).color}`}>
                          {vital.fid}ms
                        </div>
                        <div className="text-xs text-gray-500">
                          {getFIDStatus(vital.fid).label}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className={`font-medium ${getCLSStatus(vital.cls).color}`}>
                          {vital.cls.toFixed(3)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {getCLSStatus(vital.cls).label}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(vital.checked_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Web Vitals Data</h3>
          <p className="text-gray-600 mb-6">
            Start collecting Core Web Vitals data to monitor your website's performance and user experience.
          </p>
          <a
            href="https://web.dev/vitals/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Learn About Core Web Vitals
          </a>
        </div>
      )}

      {/* Legend */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Core Web Vitals Thresholds</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-900 mb-1">LCP (Largest Contentful Paint)</p>
            <ul className="space-y-1 text-gray-600">
              <li><span className="text-green-600">●</span> Good: ≤ 2.5s</li>
              <li><span className="text-yellow-600">●</span> Needs Improvement: 2.5s - 4.0s</li>
              <li><span className="text-red-600">●</span> Poor: &gt; 4.0s</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-gray-900 mb-1">FID (First Input Delay)</p>
            <ul className="space-y-1 text-gray-600">
              <li><span className="text-green-600">●</span> Good: ≤ 100ms</li>
              <li><span className="text-yellow-600">●</span> Needs Improvement: 100ms - 300ms</li>
              <li><span className="text-red-600">●</span> Poor: &gt; 300ms</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-gray-900 mb-1">CLS (Cumulative Layout Shift)</p>
            <ul className="space-y-1 text-gray-600">
              <li><span className="text-green-600">●</span> Good: ≤ 0.1</li>
              <li><span className="text-yellow-600">●</span> Needs Improvement: 0.1 - 0.25</li>
              <li><span className="text-red-600">●</span> Poor: &gt; 0.25</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
